import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { __t } from '../../../utils/t';
import { HoverTooltip } from '../../HoverTooltip';
import { Icon } from '../../Icon';

import './styles.scss';

interface Props {
    idFrom: string;
    idTo: string;
    fromValue: string;
    toValue: string;
    className?: string;
    minValue?: string;
    maxValue?: string;
    error?: string;
    disabled?: boolean;
    removeBorderRadius?: 'top' | 'bottom';
    onChange: (id: string, value: string) => void;
}

export const Range = ({
    idFrom,
    idTo,
    fromValue,
    toValue,
    className = '',
    minValue,
    maxValue,
    error,
    disabled,
    removeBorderRadius,
    onChange,
}: Props) => {
    const [rangeToDisabled, setRangeToDisabled] = useState(true);

    useEffect(() => {
        if (disabled) {
            return;
        }
        if (!fromValue) {
            setRangeToDisabled(true);
        }
        if (fromValue || toValue) {
            setRangeToDisabled(false);
        }
    }, [disabled, fromValue, toValue]);

    const onRangeFromChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.value) {
            onChange(idTo, '');
        }

        onChange(idFrom, e.target.value);
    };

    const onRangeFromBlur = () => {
        if (minValue && parseFloat(fromValue) < parseFloat(minValue)) {
            onChange(idFrom, minValue);
        }
    };

    const onRangeToChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
        onChange(idTo, e.target.value);
    };

    const onRangeToBlur = () => {
        if (parseFloat(toValue) < parseFloat(fromValue)) {
            onChange(idTo, fromValue);
            return;
        }
        if (maxValue && parseFloat(toValue) > parseFloat(maxValue)) {
            onChange(idTo, maxValue);
            return;
        }
    };

    const borderRadiusClassName = useMemo(() => {
        switch (removeBorderRadius) {
            case 'top':
                return 'app-range-input__input-container--no-top-border';
            case 'bottom':
                return 'app-range-input__input-container--no-bottom-border';
            default:
                return '';
        }
    }, [removeBorderRadius]);

    return (
        <div className={`app-range-input ${className}`}>
            <div className='app-range-input__wrap'>
                <div
                    className={`app-range-input__input-container ${
                        disabled ? 'app-range-input__input-container--disabled' : ''
                    } ${borderRadiusClassName}`}
                >
                    <label className='app-range-input__input-prefix' htmlFor={idFrom}>
                        {__t('От')}
                    </label>
                    <input
                        id={idFrom}
                        type='number'
                        inputMode='decimal'
                        className={`app-range-input__input ${error ? 'app-range-input__input--has-error' : ''}`}
                        value={fromValue}
                        disabled={disabled}
                        autoComplete='off'
                        onChange={onRangeFromChangeHandler}
                        onBlur={onRangeFromBlur}
                    />
                </div>

                <div
                    className={`app-range-input__input-container ${
                        disabled || rangeToDisabled ? 'app-range-input__input-container--disabled' : ''
                    } ${borderRadiusClassName}`}
                >
                    <label className='app-range-input__input-prefix' htmlFor={idTo}>
                        {__t('До')}
                    </label>
                    <input
                        id={idTo}
                        type='number'
                        inputMode='decimal'
                        className={`app-range-input__input ${error ? 'app-range-input__input--has-error' : ''}`}
                        value={toValue}
                        disabled={disabled || rangeToDisabled}
                        autoComplete='off'
                        onChange={onRangeToChangeHandler}
                        onBlur={onRangeToBlur}
                    />

                    <HoverTooltip className='app-range-input__not-required-tooltip' text={__t('Необязательное поле')}>
                        <Icon icon='info-circle' />
                    </HoverTooltip>
                </div>
            </div>
        </div>
    );
};
